import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { makeApiCall } from '../utils/apiConfig';
import { CircularProgress, Box, Typography } from '@mui/material';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

const OnboardingGuard: React.FC<OnboardingGuardProps> = ({ children }) => {
  const { userProfile, currentUser, isAdmin } = useAuth();
  const {
    isImpersonating: contextImpersonating,
    impersonatedUserType: contextImpersonatedUserType,
    impersonatedOrg,
  } = useImpersonation();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);

  useEffect(() => {
    const checkOnboarding = async () => {
      if (!currentUser) {
        setChecking(false);
        return;
      }

      // Fast path: Admin users bypass onboarding entirely (check locally first)
      const isLocalAdmin =
        isAdmin ||
        userProfile?.role === 'admin' ||
        userProfile?.role === 'super_admin';

      // Prefer live context; fall back to localStorage (set by ImpersonationContext)
      let isImpersonating = contextImpersonating;
      let impersonatedUserType: 'store' | 'vendor_supplier' | null =
        contextImpersonatedUserType;
      let impersonatedOrgId: string | null = impersonatedOrg?.id || null;

      if (!isImpersonating) {
        try {
          const storedImpersonation = localStorage.getItem('admin-impersonation');
          if (storedImpersonation) {
            const parsed = JSON.parse(storedImpersonation);
            if (parsed?.isImpersonating) {
              isImpersonating = true;
              impersonatedUserType =
                parsed?.userType || parsed?.impersonatedUserType || null;
              impersonatedOrgId = parsed?.org?.id || null;
            }
          }
        } catch (error) {
          console.warn('Failed to parse impersonation state from localStorage:', error);
        }
      }

      // Fast path: Admin not impersonating - skip API calls entirely
      if (isLocalAdmin && !isImpersonating) {
        console.log('✅ Admin user detected locally - bypassing onboarding (fast path)');
        setChecking(false);
        setShouldRedirect(false);
        setRedirectPath(null);
        return;
      }

      const { getAuthHeaders } = await import('../utils/apiConfig');
      const headers = await getAuthHeaders();

      const response = await makeApiCall('/onboarding/status', {
        headers,
      });

      if (!response.ok) {
        console.error('Failed to check onboarding status');
        setChecking(false);
        return;
      }

      const data = await response.json();
      const canAccess = data.data?.access;
      const onboardingState = data.data?.onboardingState;
      
      const isAdminUser =
        data.data?.isAdmin ||
        userProfile?.role === 'admin' ||
        userProfile?.role === 'super_admin';

      const impersonatedType = impersonatedUserType;
      const isAdminImpersonatingWithOrg = isAdminUser && impersonatedOrgId;
      const isAdminImpersonatingGeneric = isAdminUser && isImpersonating && !impersonatedOrgId;

      // Admin not impersonating at all - bypass onboarding
      if (isAdminUser && !isImpersonating) {
        console.log('✅ Admin user detected - bypassing onboarding');
        setChecking(false);
        return;
      }

      // Admin doing generic role impersonation (no specific org) - bypass onboarding
      // They're just testing the UI, not actual org data
      if (isAdminImpersonatingGeneric) {
        console.log('✅ Admin doing generic impersonation - bypassing onboarding');
        setChecking(false);
        return;
      }

      // If admin is impersonating a store/vendor with a specific org, verify the org exists
      if (isAdminImpersonatingWithOrg && impersonatedOrgId) {
        try {
          const impersonationResponse = await makeApiCall(`/admin/organizations/${impersonatedOrgId}`, {
            headers,
          });
          if (impersonationResponse.ok) {
            const orgData = await impersonationResponse.json();
            if (!orgData?.data) {
              console.warn('Impersonated organization not found, skipping onboarding check');
              setChecking(false);
              return;
            }
            // /onboarding/status describes the logged-in admin, not the impersonated store/vendor org.
            // Without this return, !canAccess wrongly sends admins to setup-store while impersonating.
            console.log('✅ Admin impersonating a verified org — bypassing onboarding');
            setChecking(false);
            return;
          } else {
            console.warn('Failed to verify impersonated organization, skipping onboarding check');
            setChecking(false);
            return;
          }
        } catch (error) {
          console.error('Error validating impersonated organization:', error);
          setChecking(false);
          return;
        }
      }

      // If user can't access app (non-admin or impersonated store/vendor), check next step
      if (!canAccess) {
        const nextStepResponse = await makeApiCall('/onboarding/next-step', {
          headers,
        });
        if (nextStepResponse.ok) {
          const nextStepData = await nextStepResponse.json();
          const nextPath = nextStepData.data?.nextStep || '/onboarding/terms';
          setRedirectPath(nextPath);
          setShouldRedirect(true);
        } else {
          // Fallback: check locally based on userProfile
          // First check terms acceptance
          if (!userProfile?.termsAcceptance?.termsAccepted) {
            setRedirectPath('/onboarding/terms');
            setShouldRedirect(true);
          } else if (!userProfile?.userType && !impersonatedType) {
            setRedirectPath('/onboarding/select-type');
            setShouldRedirect(true);
          } else if (
            (!(userProfile?.organizationId || userProfile?.primaryOrganizationId) && !isAdminImpersonatingWithOrg) ||
            (isAdminImpersonatingWithOrg && !impersonatedOrgId)
          ) {
            const effectiveType = impersonatedType || userProfile?.userType;
            if (effectiveType === 'store') {
              setRedirectPath('/onboarding/setup-store');
            } else if (effectiveType === 'vendor_supplier') {
              setRedirectPath('/onboarding/setup-vendor');
            } else if (effectiveType === 'group') {
              setRedirectPath('/onboarding/setup-group');
            } else {
              setRedirectPath('/onboarding/select-type');
            }
            setShouldRedirect(true);
          }
        }
      }

      // Completed all checks without early return
      setChecking(false);
    };

    checkOnboarding();
  }, [
    currentUser,
    userProfile,
    isAdmin,
    contextImpersonating,
    contextImpersonatedUserType,
    impersonatedOrg?.id,
  ]);

  useEffect(() => {
    if (!checking && shouldRedirect && redirectPath) {
      navigate(redirectPath);
    }
  }, [checking, shouldRedirect, redirectPath, navigate]);

  if (checking) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (shouldRedirect) {
    return null; // Navigation will happen via useEffect
  }

  return <>{children}</>;
};

export default OnboardingGuard;
